// ---------------------------------------------------------------------------
// Sub-basin GIS layer: the new app's "wow effect". Reads the shapefile
// declared in GisCapas (a table in the .ehd) and links it with APO_CUENCAS
// via the field GisCapas.CodID points to (verified against a real project:
// "ID_EvalHid" in the .dbf == APO_CUENCAS.gisCode, and GisCapas.CodArea
// "SupMasaCue" matches APO_CUENCAS.area exactly).
//
// shpjs already reprojects to WGS84 (lon/lat) using the .prj, ready for
// Leaflet.
// ---------------------------------------------------------------------------
import { combine, parseDbf, parseShp } from "shpjs";
import type { ReadableFolder } from "./fsReadable.ts";
import type { LatLon } from "./geo.ts";
import { lastSegments } from "./paths.ts";
import type { Aquifer, GisLayerRow, SubBasin, EvalHidProject, OutletPoint } from "./types.ts";
import { TranslatableError } from "../i18n/translate.ts";

// .cpg is optional in shpjs 6 at runtime, even though @types/shpjs 3.x's
// types still declare it required.
const readDbf = parseDbf as unknown as (dbf: ArrayBuffer) => GeoJSON.GeoJsonProperties[];

export interface SubBasinFeature {
  type: "Feature";
  properties: {
    /** Link field value (GisCapas.CodID), exactly as it comes from the .dbf. */
    idGis: unknown;
    /** APO_CUENCAS sub-basin linked by gisCode, if found. */
    subBasin: SubBasin | null;
    /** Rest of the .dbf attributes, for inspection/debugging. */
    attributes: GeoJSON.GeoJsonProperties;
  };
  geometry: GeoJSON.Geometry;
}

export interface SubBasinLayer {
  type: "FeatureCollection";
  features: SubBasinFeature[];
  /** Number of features that could be linked to an APO_CUENCAS row. */
  linked: number;
}

async function readArrayBuffer(dir: ReadableFolder, name: string): Promise<ArrayBuffer> {
  const handle = await dir.getFileHandle(name);
  const file = await handle.getFile();
  return file.arrayBuffer();
}

async function readOptionalText(dir: ReadableFolder, name: string): Promise<string | null> {
  try {
    const handle = await dir.getFileHandle(name);
    const file = await handle.getFile();
    return await file.text();
  } catch (err) {
    // "Not found" (a shapefile with no .prj at all — a real, common,
    // unremarkable case) and a genuine read failure (permission, a
    // corrupt/locked file) used to be indistinguishable here — logged now
    // (deep-review, 2026-08-29) so the latter leaves a trace instead of
    // silently degrading to "no projection info" like the former.
    console.warn(`Could not read optional GIS file "${name}":`, err);
    return null;
  }
}

/** Resolves, from the project root, the folder containing a file whose original path was absolute. */
async function resolveFolderFromPath(
  root: ReadableFolder,
  originalAbsolutePath: string
): Promise<{ folder: ReadableFolder; fileName: string } | null> {
  const [folderName, fileName] = lastSegments(originalAbsolutePath, 2);
  if (!folderName || !fileName) return null;
  try {
    const folder = await root.getDirectoryHandle(folderName);
    return { folder, fileName };
  } catch {
    return null;
  }
}

function baseWithoutExtension(fileName: string): string {
  return fileName.replace(/\.[^.]+$/, "");
}

/** Parses a shapefile (.shp + optional .dbf + optional .prj) into GeoJSON, already reprojected to WGS84. */
export function parseShapefile(
  shpBuf: ArrayBuffer,
  dbfBuf: ArrayBuffer | null,
  prj: string | null
): GeoJSON.FeatureCollection {
  const geometries = parseShp(shpBuf, prj ?? undefined);
  const properties = dbfBuf ? readDbf(dbfBuf) : geometries.map(() => ({}));
  return combine([geometries, properties]);
}

/**
 * Parses a .geojson/.json file already in WGS84 (unlike a shapefile, GeoJSON
 * doesn't bring its own projection — lon/lat is assumed, as the format
 * itself requires). A new capability compared to the original (which only
 * accepted `.shp`, confirmed in FormGIS.vb): there's no inherited semantics
 * to replicate, just validating that it's a usable FeatureCollection.
 */
export function parseGeoJSON(text: string): GeoJSON.FeatureCollection {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new TranslatableError("gisLayer.invalidJson");
  }
  if (
    typeof data !== "object" ||
    data === null ||
    (data as { type?: unknown }).type !== "FeatureCollection" ||
    !Array.isArray((data as { features?: unknown }).features)
  ) {
    throw new TranslatableError("gisLayer.notFeatureCollection");
  }
  return data as GeoJSON.FeatureCollection;
}

function normalizeName(text: string): string {
  return text.trim().toLowerCase();
}

/**
 * Matches each feature with the entity (of any type with `gisCode`) whose
 * `gisCode` matches the .dbf/GeoJSON `idField` column — or, if that finds
 * nothing, whose name (`nameOf`) matches that same field (case/whitespace
 * insensitive). Linking by code is still tried first (more reliable: a code
 * doesn't change if the sub-basin gets renamed), the name-based one is only
 * a best effort for when the chosen column isn't `gisCode` but a text field
 * (e.g. the shapefile itself brings no matching numeric field, but does
 * have a recognizable name).
 */
function linkToEntities<T extends { gisCode: number | null }>(
  geojson: GeoJSON.FeatureCollection,
  entities: T[],
  idField: string,
  nameOf: (entity: T) => string
): {
  idGis: unknown;
  entity: T | null;
  geometry: GeoJSON.Geometry;
  attributes: GeoJSON.GeoJsonProperties;
}[] {
  const byGisCode = new Map(entities.filter((e) => e.gisCode != null).map((e) => [e.gisCode, e]));
  const byName = new Map<string, T>();
  for (const e of entities) {
    const name = normalizeName(nameOf(e));
    if (name) byName.set(name, e);
  }
  return geojson.features.map((f) => {
    const idGis = f.properties?.[idField];
    let entity = idGis != null ? (byGisCode.get(Number(idGis)) ?? null) : null;
    if (!entity && idGis != null) {
      entity = byName.get(normalizeName(String(idGis))) ?? null;
    }
    return {
      idGis,
      entity,
      geometry: f.geometry as GeoJSON.Geometry,
      attributes: f.properties ?? null,
    };
  });
}

/**
 * Number of DISTINCT sub-basins/aquifers/outlet points that really found a
 * match — counting features with a non-null `entity` isn't enough: a link
 * field with few distinct values (e.g. a type/category, not a real id)
 * makes many features "match" the SAME entity repeatedly, giving an
 * inflated, misleading count (see GisLayerPanel.tsx, which uses this to
 * warn whether the chosen column makes sense). Since `linkToEntities`
 * always returns the SAME object reference for a given gisCode, a Set by
 * identity already deduplicates without needing to know each entity type's
 * id field name.
 */
function countUniqueLinked<T extends object>(matched: { entity: T | null }[]): number {
  const unique = new Set<T>();
  for (const e of matched) if (e.entity) unique.add(e.entity);
  return unique.size;
}

/** Links each feature with the APO_CUENCAS sub-basin whose gisCode (or, failing that, description) matches the .dbf's `idField`. */
export function linkToSubBasins(
  geojson: GeoJSON.FeatureCollection,
  subBasins: SubBasin[],
  idField: string
): SubBasinLayer {
  const matched = linkToEntities(geojson, subBasins, idField, (c) => c.description);
  const linked = countUniqueLinked(matched);
  const features: SubBasinFeature[] = matched.map((e) => ({
    type: "Feature",
    properties: { idGis: e.idGis, subBasin: e.entity, attributes: e.attributes },
    geometry: e.geometry,
  }));
  return { type: "FeatureCollection", features, linked };
}

export interface AquiferFeature {
  type: "Feature";
  properties: { idGis: unknown; aquifer: Aquifer | null; attributes: GeoJSON.GeoJsonProperties };
  geometry: GeoJSON.Geometry;
}

export interface AquiferLayer {
  type: "FeatureCollection";
  features: AquiferFeature[];
  linked: number;
}

/** Same as `linkToSubBasins`, but for the (polygon) aquifer layer — LayerAcuif in the original (GIS.vb). */
export function linkToAquifers(
  geojson: GeoJSON.FeatureCollection,
  aquifers: Aquifer[],
  idField: string
): AquiferLayer {
  const matched = linkToEntities(geojson, aquifers, idField, (a) => a.name);
  const linked = countUniqueLinked(matched);
  const features: AquiferFeature[] = matched.map((e) => ({
    type: "Feature",
    properties: { idGis: e.idGis, aquifer: e.entity, attributes: e.attributes },
    geometry: e.geometry,
  }));
  return { type: "FeatureCollection", features, linked };
}

export interface OutletPointFeature {
  type: "Feature";
  properties: {
    idGis: unknown;
    outletPoint: OutletPoint | null;
    attributes: GeoJSON.GeoJsonProperties;
  };
  geometry: GeoJSON.Geometry;
}

export interface OutletPointLayer {
  type: "FeatureCollection";
  features: OutletPointFeature[];
  linked: number;
}

/** Same as `linkToSubBasins`, but for the (point) outlet-points layer — LayerPDSuper in the original (GIS.vb). */
export function linkToOutletPoints(
  geojson: GeoJSON.FeatureCollection,
  outletPoints: OutletPoint[],
  idField: string
): OutletPointLayer {
  const matched = linkToEntities(geojson, outletPoints, idField, (p) => p.name);
  const linked = countUniqueLinked(matched);
  const features: OutletPointFeature[] = matched.map((e) => ({
    type: "Feature",
    properties: { idGis: e.idGis, outletPoint: e.entity, attributes: e.attributes },
    geometry: e.geometry,
  }));
  return { type: "FeatureCollection", features, linked };
}

/** Loads the first sub-basin layer declared in GisCapas and links it with APO_CUENCAS. */
export async function loadSubBasinLayer(
  root: ReadableFolder,
  project: EvalHidProject
): Promise<SubBasinLayer | null> {
  const layer: GisLayerRow | undefined = project.gisLayers[0];
  if (!layer?.shapefile) return null;

  const resolved = await resolveFolderFromPath(root, layer.shapefile);
  if (!resolved) return null;
  const { folder, fileName } = resolved;
  const base = baseWithoutExtension(fileName);

  let shpBuf: ArrayBuffer;
  try {
    shpBuf = await readArrayBuffer(folder, `${base}.shp`);
  } catch {
    return null;
  }
  const [dbfBuf, prj] = await Promise.all([
    // Same reasoning as readOptionalText's own catch — see its comment.
    readArrayBuffer(folder, `${base}.dbf`).catch((err) => {
      console.warn(`Could not read optional GIS file "${base}.dbf":`, err);
      return null;
    }),
    readOptionalText(folder, `${base}.prj`),
  ]);

  const geojson = parseShapefile(shpBuf, dbfBuf, prj);
  const idField = layer.idCode ?? "ID_EvalHid";
  return linkToSubBasins(geojson, project.subBasins, idField);
}

// ---------------------------------------------------------------------------
// Helper geometry: bounds (bounding box) of a GeoJSON geometry or a set of
// features, as [[lat,lng],[lat,lng]] (the format Leaflet uses). Shared
// between the normal map (SubBasinMap) and the wizard's selection map
// (AssignmentMap, which also uses it per-feature for the selection
// rectangle).
// ---------------------------------------------------------------------------
type Ring = [number, number][];
type Coordinates = Ring | Ring[] | Ring[][];
type LatLngBounds = [[number, number], [number, number]];

function visitCoordinates(
  coords: Coordinates | [number, number],
  visit: (lng: number, lat: number) => void
): void {
  if (typeof coords[0] === "number") {
    const [lng, lat] = coords as [number, number];
    visit(lng, lat);
    return;
  }
  (coords as Coordinates[]).forEach((c) => visitCoordinates(c, visit));
}

/** Bounds of a single geometry (Polygon/MultiPolygon; GeometryCollection isn't used in EvalHid). */
export function geometryBounds(geometry: GeoJSON.Geometry): LatLngBounds | null {
  if (geometry.type === "GeometryCollection") return null;
  let minLat = Infinity,
    minLng = Infinity,
    maxLat = -Infinity,
    maxLng = -Infinity;
  visitCoordinates(geometry.coordinates as unknown as Coordinates, (lng, lat) => {
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
  });
  if (!Number.isFinite(minLat)) return null;
  return [
    [minLat, minLng],
    [maxLat, maxLng],
  ];
}

// ---------------------------------------------------------------------------
// Geometry drawn by hand on the map (see MapDrawing.tsx): builds a
// single-feature GeoJSON from the vertices the user has clicked, ready to
// pass to importAdditionalSubBasins()/importOutletPointsFromGeo()/
// importAquifersFromGeo() — the same path as an imported shapefile, just
// with a single hand-drawn geometry instead of a file.
// ---------------------------------------------------------------------------

/** Single-geometry FeatureCollection, with the properties the `import*FromGeo`/`importAdditionalSubBasins` functions expect (`Name`, and `Area` for sub-basins). */
export function drawnFeature(
  geometry: GeoJSON.Point | GeoJSON.Polygon,
  name: string,
  areaKm2?: number
): GeoJSON.FeatureCollection {
  return {
    type: "FeatureCollection",
    features: [
      {
        type: "Feature",
        properties: { Name: name, ...(areaKm2 != null ? { Area: areaKm2 } : {}) },
        geometry,
      },
    ],
  };
}

/**
 * Approximate area (km²) of a ring of `[lng, lat]` vertices, via the
 * shoelace formula over an equirectangular projection centered on the
 * ring's own mean latitude — good enough as an editable initial value when
 * drawing a sub-basin by hand (not geodesically exact, but the error is
 * negligible at a real sub-basin's scale; the user can correct it
 * afterward with the regular edit dialog).
 */
export function approximateAreaKm2(ring: [number, number][]): number {
  if (ring.length < 3) return 0;
  const EARTH_RADIUS_KM = 6371;
  const meanLatRad = (ring.reduce((sum, [, lat]) => sum + lat, 0) / ring.length) * (Math.PI / 180);
  const points = ring.map(([lng, lat]) => [
    ((lng * Math.PI) / 180) * EARTH_RADIUS_KM * Math.cos(meanLatRad),
    ((lat * Math.PI) / 180) * EARTH_RADIUS_KM,
  ]);
  let area2 = 0;
  for (let i = 0; i < points.length; i++) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % points.length];
    area2 += x1 * y2 - x2 * y1;
  }
  return Math.abs(area2) / 2;
}

/**
 * Same as approximateAreaKm2, but for a whole Polygon/MultiPolygon geometry
 * (only the outer ring of each part — holes are a negligible correction at
 * the scale this is used for, a reference layer's tooltip).
 */
export function approximateGeometryAreaKm2(geometry: GeoJSON.Geometry): number {
  if (geometry.type === "Polygon") {
    return approximateAreaKm2(geometry.coordinates[0] as [number, number][]);
  }
  if (geometry.type === "MultiPolygon") {
    return geometry.coordinates.reduce(
      (sum, polygon) => sum + approximateAreaKm2(polygon[0] as [number, number][]),
      0
    );
  }
  return 0;
}

// ---------------------------------------------------------------------------
// Centroid — a representative point for a sub-basin's geometry, used by
// aemet.ts to pick the nearest weather station(s). Not meant to be
// geodesically exact (same spirit as approximateAreaKm2): the bounding-box
// midpoint already available (geometryBounds) is cheaper but centers badly
// on elongated/L-shaped basins, very common for river sub-basins, which
// would bias which station gets picked — a real area-weighted centroid
// avoids that.
// ---------------------------------------------------------------------------

/**
 * Signed-area-weighted centroid of a ring (closed or open — no need to
 * repeat the first point at the end), in the same local equirectangular
 * projection (centered on the ring's own mean latitude) as
 * `approximateAreaKm2`, so the two stay consistent with each other.
 */
function ringCentroid(ring: [number, number][]): LatLon | null {
  if (ring.length < 3) return null;
  const EARTH_RADIUS_KM = 6371;
  const meanLatRad = (ring.reduce((sum, [, lat]) => sum + lat, 0) / ring.length) * (Math.PI / 180);
  const points = ring.map(([lng, lat]) => [
    ((lng * Math.PI) / 180) * EARTH_RADIUS_KM * Math.cos(meanLatRad),
    ((lat * Math.PI) / 180) * EARTH_RADIUS_KM,
  ]);
  let signedArea = 0;
  let cx = 0;
  let cy = 0;
  for (let i = 0; i < points.length; i++) {
    const [x0, y0] = points[i];
    const [x1, y1] = points[(i + 1) % points.length];
    const cross = x0 * y1 - x1 * y0;
    signedArea += cross;
    cx += (x0 + x1) * cross;
    cy += (y0 + y1) * cross;
  }
  signedArea /= 2;
  if (signedArea === 0) return null;
  cx /= 6 * signedArea;
  cy /= 6 * signedArea;
  return {
    lat: (cy / EARTH_RADIUS_KM) * (180 / Math.PI),
    lon: (cx / (EARTH_RADIUS_KM * Math.cos(meanLatRad))) * (180 / Math.PI),
  };
}

/**
 * Centroid of a Polygon/MultiPolygon geometry — outer ring of each part only
 * (holes are a negligible correction at this use case's scale). A
 * MultiPolygon's parts are averaged weighted by their own area, so a small
 * disconnected sliver doesn't pull the point away from the main body.
 */
export function geometryCentroid(geometry: GeoJSON.Geometry): LatLon | null {
  if (geometry.type === "Polygon") {
    return ringCentroid(geometry.coordinates[0] as [number, number][]);
  }
  if (geometry.type === "MultiPolygon") {
    const parts = geometry.coordinates
      .map((polygon) => {
        const ring = polygon[0] as [number, number][];
        const centroid = ringCentroid(ring);
        const area = approximateAreaKm2(ring);
        return centroid && area > 0 ? { centroid, area } : null;
      })
      .filter((p): p is { centroid: LatLon; area: number } => p !== null);
    if (parts.length === 0) return null;
    const totalArea = parts.reduce((sum, p) => sum + p.area, 0);
    return {
      lat: parts.reduce((sum, p) => sum + p.centroid.lat * p.area, 0) / totalArea,
      lon: parts.reduce((sum, p) => sum + p.centroid.lon * p.area, 0) / totalArea,
    };
  }
  return null;
}

function findSubBasinFeature(
  layer: SubBasinLayer,
  subBasinCode: number
): SubBasinFeature | undefined {
  return layer.features.find((f) => f.properties.subBasin?.subBasinCode === subBasinCode);
}

/** Centroid of the sub-basin `subBasinCode`'s geometry in `layer`, or `null` if it isn't linked to any feature there (no geometry to derive a point from). */
export function subBasinCentroid(layer: SubBasinLayer, subBasinCode: number): LatLon | null {
  const feature = findSubBasinFeature(layer, subBasinCode);
  return feature ? geometryCentroid(feature.geometry) : null;
}

/**
 * The sub-basin's own real polygon/multipolygon geometry (not just its
 * centroid) — needed for the ROCIO grid method (rocio.ts's `cellsInPolygon`),
 * which selects every grid cell actually falling inside the sub-basin's
 * footprint rather than approximating from a single point.
 */
export function subBasinGeometry(
  layer: SubBasinLayer,
  subBasinCode: number
): GeoJSON.Geometry | null {
  return findSubBasinFeature(layer, subBasinCode)?.geometry ?? null;
}

/** Ray-casting test, `lng`/`lat` in the ring's own units — true if odd number of crossings. */
function pointInRing(lng: number, lat: number, ring: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) inside = !inside;
  }
  return inside;
}

function pointInPolygonRings(lng: number, lat: number, rings: [number, number][][]): boolean {
  const [outer, ...holes] = rings;
  if (!pointInRing(lng, lat, outer)) return false;
  return !holes.some((hole) => pointInRing(lng, lat, hole));
}

/**
 * Point-in-polygon test (Polygon/MultiPolygon, holes subtract) — same
 * planar-approximation spirit as approximateAreaKm2/ringCentroid, not
 * geodesically exact but plenty for basin-scale MITECO polygons. Used to
 * find which sub-basins fall inside a selected basin (mitecoLayers.ts's
 * `findSubBasinsInBasin`) by testing each sub-basin's own centroid rather
 * than a full polygon intersection — cheap, and correct except for a
 * sub-basin that straddles the basin boundary with most of its area on one
 * side and its centroid on the other, a rare edge case at this scale.
 */
export function pointInPolygon(point: LatLon, geometry: GeoJSON.Geometry): boolean {
  if (geometry.type === "Polygon") {
    return pointInPolygonRings(point.lon, point.lat, geometry.coordinates as [number, number][][]);
  }
  if (geometry.type === "MultiPolygon") {
    return (geometry.coordinates as [number, number][][][]).some((rings) =>
      pointInPolygonRings(point.lon, point.lat, rings)
    );
  }
  return false;
}

// ---------------------------------------------------------------------------
// Compare geometries by value — for recovering from GIS after a permanent
// delete (see projectEntities.ts) without duplicating what's still in the
// project: when re-importing the original full shapefile/GeoJSON, only the
// features whose geometry does NOT already match an existing one matter.
// ---------------------------------------------------------------------------

function roundCoordinates(coords: unknown): unknown {
  if (typeof coords === "number") return Math.round(coords * 1e7) / 1e7;
  if (Array.isArray(coords)) return coords.map(roundCoordinates);
  return coords;
}

/**
 * Stable key to compare two geometries by value, rounded to 7 decimals
 * (~1cm of precision on the surface — plenty to distinguish real
 * sub-basins/aquifers/outlet points, and absorbs any parsing noise from
 * re-importing the same original file).
 */
export function geometryKey(geometry: GeoJSON.Geometry): string {
  if (geometry.type === "GeometryCollection") return JSON.stringify(geometry);
  return `${geometry.type}:${JSON.stringify(roundCoordinates(geometry.coordinates))}`;
}

/** Set of geometry keys from a list of features — to filter out duplicates when re-importing (see geometryKey). */
export function geometriesOf(features: { geometry: GeoJSON.Geometry }[]): Set<string> {
  return new Set(features.map((f) => geometryKey(f.geometry)));
}

/**
 * Geometries of a layer (sub-basins, aquifers, outlet points) that are
 * STILL linked to a live entity in the project — unlike `layer.features`
 * plain, which keeps the geometry of an already-deleted entity (a layer
 * isn't trimmed on delete, see `deleteSubBasin` in projectEntities.ts: it
 * only removes from `project.subBasins`). Comparing against `layer.features`
 * without this filter would mean a just-deleted entity would never be
 * recognized as "missing" when recovering from GIS.
 */
export function geometriesOfLiveEntities<T extends { gisCode: number | null }>(
  layer: { features: { properties: { idGis: unknown }; geometry: GeoJSON.Geometry }[] } | null,
  entities: T[]
): Set<string> {
  if (!layer) return new Set();
  const liveGisCodes = new Set(
    entities.map((e) => e.gisCode).filter((c): c is number => c != null)
  );
  return geometriesOf(
    layer.features.filter(
      (f) => f.properties.idGis != null && liveGisCodes.has(Number(f.properties.idGis))
    )
  );
}

/**
 * Fallback for layerBounds() when there's nothing to frame yet (a brand
 * new project with no sub-basins of its own) — Spain's extent (same as
 * the MITECO basins dataset's own bounds, see mitecoLayers.ts) instead of
 * the whole world, since the map is still useful without any project
 * geometry (MITECO reference layers, hand-drawing) and starting zoomed
 * out to the whole planet would make that first click a lot harder to find.
 */
export const SPAIN_BOUNDS: LatLngBounds = [
  [36.0, -9.3],
  [43.8, 3.32],
];

/** Bounds enclosing all of a layer's features (to frame the map when loading it). */
export function layerBounds(layer: SubBasinLayer): LatLngBounds {
  let minLat = Infinity,
    minLng = Infinity,
    maxLat = -Infinity,
    maxLng = -Infinity;
  for (const f of layer.features) {
    const bounds = geometryBounds(f.geometry);
    if (!bounds) continue;
    minLat = Math.min(minLat, bounds[0][0]);
    minLng = Math.min(minLng, bounds[0][1]);
    maxLat = Math.max(maxLat, bounds[1][0]);
    maxLng = Math.max(maxLng, bounds[1][1]);
  }
  if (!Number.isFinite(minLat)) {
    return SPAIN_BOUNDS;
  }
  return [
    [minLat, minLng],
    [maxLat, maxLng],
  ];
}
